/**
 * Cache-hit vs forceRefresh delta chart — the headline visualization.
 *
 * Renders two side-by-side bars (cache hit vs force refresh) keyed on
 * avg cost per audit. The delta percentage is the focal metric: the May
 * 2026 incident hypothesis (per A7Y-1487) was that force_refresh audits
 * cost meaningfully more, so making the gap glanceable is the point.
 */
import { ViewQueryButton } from "./ViewQueryButton";
import { formatPct, formatUsd } from "./format";
import type { ApiEnvelope, CacheDeltaData } from "./types";

interface CacheDeltaChartProps {
  envelope: ApiEnvelope<CacheDeltaData> | undefined;
  loading: boolean;
}

function Bar({
  label,
  value,
  count,
  color,
  p50,
  p95,
  max,
}: {
  label: string;
  value: number;
  count: number;
  color: string;
  p50: number;
  p95: number;
  max: number;
}) {
  const widthPct = (value / max) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {label}
        </span>
        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          {formatUsd(value)}
        </span>
      </div>
      <div className="h-8 w-full rounded bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
        <div
          className={`h-full ${color} transition-[width] duration-500`}
          style={{ width: `${widthPct.toFixed(2)}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-zinc-500">
        {count} audits • p50 {formatUsd(p50)} • p95 {formatUsd(p95)}
      </div>
    </div>
  );
}

export function CacheDeltaChart({ envelope, loading }: CacheDeltaChartProps) {
  const data = envelope?.data;
  const cacheHit = data?.rows.find((r) => r.bucket === "cache_hit");
  const forceRefresh = data?.rows.find((r) => r.bucket === "force_refresh");
  const max = Math.max(cacheHit?.avg_cost ?? 0, forceRefresh?.avg_cost ?? 0, 0.0001);

  const noForceRefresh = (forceRefresh?.audit_count ?? 0) === 0;

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Cache hit vs force refresh (7d)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Average cost per audit, bucketed by whether the audit bypassed caches.
          </p>
        </div>
        {data && (
          <div className="text-right">
            <div className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              {data.delta_pct >= 0 ? "+" : ""}
              {formatPct(data.delta_pct, 1)}
            </div>
            <div className="text-xs text-zinc-500">
              {data.delta_abs >= 0 ? "force_refresh costs more" : "force_refresh costs less"}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-zinc-500">loading…</div>
      ) : noForceRefresh && !cacheHit ? (
        <div className="py-8 text-center text-sm text-zinc-500">
          No audits in the last 7 days.
        </div>
      ) : (
        <div className="space-y-4">
          <Bar
            label="Cache hit"
            value={cacheHit?.avg_cost ?? 0}
            count={cacheHit?.audit_count ?? 0}
            p50={cacheHit?.p50 ?? 0}
            p95={cacheHit?.p95 ?? 0}
            color="bg-emerald-500"
            max={max}
          />
          <Bar
            label="Force refresh"
            value={forceRefresh?.avg_cost ?? 0}
            count={forceRefresh?.audit_count ?? 0}
            p50={forceRefresh?.p50 ?? 0}
            p95={forceRefresh?.p95 ?? 0}
            color="bg-amber-500"
            max={max}
          />
          {noForceRefresh && (
            <p className="text-xs text-zinc-500 italic">
              No force_refresh audits in this window — trigger one via the audit
              API with <code>forceRefresh: true</code> to populate.
            </p>
          )}
        </div>
      )}

      <div className="mt-3">
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
